// The Insights drawer every page opens (Tables, dashboards, AI budgets,
// visualisations, Theme AI's code-frame agent). Drawn with SidePaneChat, so
// it looks and behaves like the main agent conversation (Adam, 23 Sep: "style
// all agent drawers the same as the main agent message list").
//
// The prop names are the ones ChatSession took, unchanged, so every caller and
// every test that mocks this file by path keeps working.
import React, { useState } from 'react'
import axios from 'axios'
import { Drawer } from '@mui/material'
import { SidePaneChat, type ComposerModel } from '../../agent'
import useTranscribeEndpoint from '../../hooks/useTranscribeEndpoint'
import useDictationAccess from '../../hooks/useDictationAccess'
import useAccount from '../../hooks/useAccount'
import { toSidePaneMessages } from './adapters'
import PersonaSelect from './PersonaSelect'
import { STANDARD_PERSONA_NAMES } from '../../hooks/usePersonas'
import type { ChatPaneProps } from './types'
import { HEADER_RULE_PX } from '../../theme'

export type { ChatMessage, ToolCall } from './types'

export interface ChatDrawerWidth {
  width: string | number
  minWidth?: number
  maxWidth?: number
}

export interface ChatDrawerProps extends ChatPaneProps {
  open?: boolean
  /** The drawer's width. Default: 35% of the window, 320–600px. */
  width?: ChatDrawerWidth
  /** The drawer's heading. Default: "Insights". */
  title?: string
  /**
   * 'temporary' (the default) is modal: the page behind is inert. The Help
   * pane is 'persistent' so the person can use the page, and its outline can
   * find the page's controls, while the pane is open.
   */
  variant?: 'temporary' | 'persistent'
  /** Attributes on the drawer's paper, e.g. data-voice="off". */
  paperProps?: Record<string, string>
  /**
   * Mark the drawer as apart from the page (the Help pane): an info-blue left
   * edge as thick as the header's gold rule, and a shadow onto the page. No
   * overlay: the page stays usable.
   */
  accent?: boolean
  /** A colour for the composer card's border (a theme path such as 'info.main' works). Absent: the usual hairline. */
  composerAccent?: string
  /** The first row of the conversation while it is empty (the Help pane's greeting). */
  greeting?: string
}

/** The accented drawer's shadow onto the page to its left. */
const ACCENT_SHADOW = '-16px 0 40px rgba(10,22,40,0.22)'

/**
 * The Insights drawer's width: 35% of the window, 320–600px. The story board
 * editor's agent panel takes the same (Adam, 27 Sep: "exactly the same width
 * as the one that we use for tables").
 */
export const INSIGHTS_DRAWER_WIDTH: ChatDrawerWidth = { width: '35%', minWidth: 320, maxWidth: 600 }

/** The same width as one CSS length, for a page that makes room beside the drawer. */
export const INSIGHTS_DRAWER_CSS_WIDTH = `clamp(${INSIGHTS_DRAWER_WIDTH.minWidth}px, 35vw, ${INSIGHTS_DRAWER_WIDTH.maxWidth}px)`

// The model ids ChatSession sent, unchanged: Normal is gpt-4.1, Advanced gpt-5.
const NORMAL_MODEL = 'gpt-4.1'
const ADVANCED_MODEL = 'gpt-5'
const MODE_MODELS: ComposerModel[] = [
  { id: NORMAL_MODEL, label: 'Normal' },
  { id: ADVANCED_MODEL, label: 'Advanced' },
]
const NO_MODELS: ComposerModel[] = []

const ChatDrawer: React.FC<ChatDrawerProps> = ({
  open,
  width = INSIGHTS_DRAWER_WIDTH,
  title = 'Insights',
  messages,
  onSendMessage,
  onResetChat,
  onClose,
  isLoading = false,
  error,
  loadingTitle,
  showHeader = true,
  placeholder = 'Ask about this table...',
  showAdvancedMode = true,
  examplePrompts,
  // Kept for callers; a prompt pill always sends now (it used to be inert
  // without this, which read as a broken button).
  autoTriggerExamplePrompts: _autoTriggerExamplePrompts,
  disabled = false,
  renderMessage,
  // Pages pass the personas library (usePersonas, #1523); without a list the
  // pill offers the built-ins only.
  personas = STANDARD_PERSONA_NAMES,
  selectedPersona = 'Executive Summarizer',
  onPersonaChange,
  additionalControls,
  inputHelper,
  scopeLabel,
  framing,
  variant = 'temporary',
  paperProps,
  accent = false,
  composerAccent,
  greeting,
}) => {
  const [model, setModel] = useState(NORMAL_MODEL)
  const currentModel = showAdvancedMode ? model : NORMAL_MODEL

  const auth = axios.defaults.headers.common['Authorization']
  const transcribeEndpoint = useTranscribeEndpoint()
  // Dictation needs agent access and budget (audit C23); the Help pane and the
  // insights drawers open below it, so the mic says why it is off.
  const dictation = useDictationAccess(useAccount().customer?.name)
  const showPersonas = personas.length > 0 && Boolean(onPersonaChange)
  const leading = showPersonas || additionalControls ? (
    <>
      {additionalControls}
      {showPersonas && (
        <PersonaSelect
          personas={personas}
          value={selectedPersona}
          onChange={(p) => onPersonaChange?.(p)}
          disabled={isLoading || disabled}
        />
      )}
    </>
  ) : undefined

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant={variant}
      PaperProps={paperProps as object | undefined}
      sx={{
        '& .MuiDrawer-paper': {
          width: width.width,
          minWidth: width.minWidth,
          maxWidth: width.maxWidth,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
          ...(accent
            ? { borderLeft: `${HEADER_RULE_PX}px solid`, borderLeftColor: 'primary.main', boxShadow: ACCENT_SHADOW }
            : {}),
        },
      }}
    >
      <SidePaneChat
        title={showHeader ? title : undefined}
        scopeLabel={scopeLabel}
        onClose={showHeader ? onClose : undefined}
        onReset={showHeader && onResetChat ? () => { onResetChat() } : undefined}
        framing={framing}
        greeting={greeting}
        messages={toSidePaneMessages(messages, renderMessage)}
        loading={isLoading}
        loadingTitle={loadingTitle}
        error={error}
        prompts={examplePrompts}
        onSend={(text, m) => onSendMessage(m || currentModel, text, selectedPersona)}
        models={showAdvancedMode ? MODE_MODELS : NO_MODELS}
        selectedModel={currentModel}
        onModelChange={setModel}
        composerLeading={leading}
        aboveComposer={inputHelper}
        placeholder={placeholder}
        disabled={disabled}
        transcribeEndpoint={transcribeEndpoint}
        dictationDisabledReason={dictation.reason}
        authHeader={typeof auth === 'string' ? auth : undefined}
        composerAccent={composerAccent}
      />
    </Drawer>
  )
}

export default ChatDrawer
